// HR-004 follow-up — Machine Groups admin.
//
// "1 operator covers N machines" config. Mill admin defines which
// machines bundle under one operator (e.g. Finisher Drawframe #1..#4 =
// 1 Tenter). The Worker Allocation board renders ONE seat per group
// instead of N seats per machine.
//
// Embedded on the Sanctioned Strength page (HR menu).

import { useEffect, useMemo, useState } from 'react'
import {
  App, Alert, Button, Card, DatePicker, Form, Input, InputNumber, Modal,
  Popconfirm, Select, Space, Switch, Table, Tag, Typography,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  machineGroupsApi, workerRolesApi, MACHINE_TYPES,
  type MachineGroup, type MachineGroupCreate, type MachineGroupPatch,
} from '@/features/hr/manpowerApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { machinesApi } from '@/features/masters/machines/api'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { Machine } from '@/shared/types/master'

const { Text } = Typography


export default function MachineGroupsCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<MachineGroup | null>(null)

  const { data: depts } = useQuery({
    queryKey: ['mg-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: machinesData } = useQuery({
    queryKey: ['mg-machines', plantId],
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
    enabled: !!plantId,
  })
  const { data: groups, isLoading } = useQuery({
    queryKey: ['machine-groups', plantId],
    queryFn: () => machineGroupsApi.list({ plant_id: plantId! }),
    enabled: !!plantId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['machine-groups'] })

  const createMut = useMutation({
    mutationFn: (body: MachineGroupCreate) => machineGroupsApi.create(body),
    onSuccess: () => { message.success('Group created'); invalidate(); setCreateOpen(false) },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: MachineGroupPatch }) =>
      machineGroupsApi.patch(id, body),
    onSuccess: () => { message.success('Updated'); invalidate(); setEditing(null) },
  })

  const membersMut = useMutation({
    mutationFn: ({ id, machine_ids }: { id: string; machine_ids: string[] }) =>
      machineGroupsApi.setMembers(id, machine_ids),
    onSuccess: () => { message.success('Members updated'); invalidate() },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const removeMut = useMutation({
    mutationFn: (id: string) => machineGroupsApi.remove(id),
    onSuccess: () => { message.success('Deactivated'); invalidate() },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Delete failed')
    },
  })

  const machineById = useMemo(() => {
    const m = new Map<string, Machine>()
    for (const x of (machinesData?.items ?? [])) m.set(x.id, x)
    return m
  }, [machinesData])
  const deptById = useMemo(() => {
    const m = new Map<string, string>()
    for (const d of (depts?.items ?? [])) m.set(d.id, d.name)
    return m
  }, [depts])

  const columns = [
    {
      title: 'Department', key: 'dept',
      render: (_: unknown, r: MachineGroup) => deptById.get(r.department_id) ?? '—',
    },
    {
      title: 'Code', dataIndex: 'group_code', key: 'code',
      render: (s: string) => <Tag color="blue">{s}</Tag>,
    },
    { title: 'Name', dataIndex: 'group_name', key: 'name' },
    {
      title: 'Type', dataIndex: 'machine_type', key: 'mt',
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: 'Role', dataIndex: 'role_code', key: 'role',
      render: (s: string) => <Tag color="purple" style={{ fontWeight: 700 }}>{s}</Tag>,
    },
    {
      title: 'Machines', key: 'machines',
      render: (_: unknown, r: MachineGroup) => (
        <Space size={2} wrap>
          {r.machine_ids.map((mid) => {
            const m = machineById.get(mid)
            return <Tag key={mid} style={{ margin: 1 }}>
              {m ? m.machine_id_str : mid.slice(0, 6)}
            </Tag>
          })}
          {r.machine_ids.length === 0 && <Text type="secondary">none</Text>}
        </Space>
      ),
    },
    {
      title: 'Req Ops', dataIndex: 'required_operator_count', key: 'req',
      render: (n: number) => (
        <Text strong>{n} op{n === 1 ? '' : 's'} for {/* compute below */}</Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'st',
      render: (b: boolean) => (
        <Tag color={b ? 'success' : 'default'}>{b ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    ...(canEdit ? [{
      title: 'Actions', key: 'actions',
      render: (_: unknown, r: MachineGroup) => (
        <Space size={2}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => setEditing(r)} />
          <Popconfirm
            title="Deactivate this group?"
            description="Worker allocations referencing it stay intact."
            onConfirm={() => removeMut.mutate(r.id)}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ]

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Space>
          <span>Machine Groups</span>
          <Tag color="processing">1 Operator covers N machines</Tag>
        </Space>
      }
      extra={canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          New Group
        </Button>
      )}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        For sections where one operator covers multiple machines — Cards, Drawing,
        Combing, Simplex, Link Coner. Example: <b>Finisher Drawframe #1..#4 = 1 Tenter</b>.
        Ring Frame stays per-machine and doesn't use groups.
      </Text>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns as never}
        dataSource={groups ?? []}
        pagination={false}
        size="small"
      />

      <CreateGroupModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        depts={depts?.items ?? []}
        machines={machinesData?.items ?? []}
        existingGroups={groups ?? []}
        onSubmit={(body) => createMut.mutate(body)}
        submitting={createMut.isPending}
      />
      <EditGroupModal
        group={editing}
        onClose={() => setEditing(null)}
        machines={machinesData?.items ?? []}
        existingGroups={groups ?? []}
        onSubmitPatch={(id, body) => patchMut.mutate({ id, body })}
        onSubmitMembers={(id, ids) => membersMut.mutate({ id, machine_ids: ids })}
        submitting={patchMut.isPending || membersMut.isPending}
      />
    </Card>
  )
}


function CreateGroupModal({
  open, onClose, depts, machines, existingGroups, onSubmit, submitting,
}: {
  open: boolean
  onClose: () => void
  depts: Array<{ id: string; name: string }>
  machines: Machine[]
  existingGroups: MachineGroup[]
  onSubmit: (body: MachineGroupCreate) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const { plantId } = usePlantId()
  const [deptId, setDeptId] = useState<string | undefined>()
  const [machineType, setMachineType] = useState<string | undefined>()

  // Worker roles for the role picker (filtered to machine-bound + both)
  const { data: workerRoles } = useQuery({
    queryKey: ['mg-worker-roles', plantId],
    queryFn: () => workerRolesApi.list({
      plant_id: plantId ?? undefined, is_active: true,
    }),
    enabled: open && !!plantId,
  })
  const eligibleRoles = useMemo(() =>
    (workerRoles ?? []).filter((r) =>
      // For machine groups, OPERATOR is the typical role, but GAITER /
      // HELPER are also valid (e.g. "1 GAITER covers 4 RF machines").
      // SWEEPER / JOBBER / RELIEVER are truly roaming and belong in
      // Department Floating Roles, not here.
      r.role_code === 'OPERATOR' || r.role_code === 'GAITER'
      || r.role_code === 'HELPER' || r.role_type === 'BOTH'
    ),
  [workerRoles])

  // Machines already in an active group — hide from the picker
  const usedMachineIds = useMemo(() => {
    const s = new Set<string>()
    for (const g of existingGroups) {
      if (g.is_active) for (const mid of g.machine_ids) s.add(mid)
    }
    return s
  }, [existingGroups])

  // HR-004 follow-up — derive the machine_type dropdown from the actual
  // machines in the picked department. Was: full MACHINE_TYPES list (16
  // options) regardless of dept. Now: only types that have machines in
  // this dept, with counts shown. Prevents the "I picked WINDING but my
  // Autoconer machines didn't appear" confusion.
  const machineTypesForDept = useMemo(() => {
    if (!deptId) return []
    const counts = new Map<string, number>()
    for (const m of machines) {
      if (m.department_id !== deptId) continue
      if (usedMachineIds.has(m.id)) continue  // already in another group
      counts.set(m.section, (counts.get(m.section) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])  // most-machines first
      .map(([t, n]) => ({ value: t, label: `${t} (${n} machine${n === 1 ? '' : 's'})` }))
  }, [deptId, machines, usedMachineIds])

  // Auto-pick machine_type when the dept has only one type available
  useEffect(() => {
    if (machineTypesForDept.length === 1 && !machineType) {
      setMachineType(machineTypesForDept[0].value)
      form.setFieldsValue({ machine_type: machineTypesForDept[0].value })
    }
  }, [machineTypesForDept, machineType, form])

  const eligibleMachines = useMemo(() => {
    return machines.filter((m) =>
      (!deptId || m.department_id === deptId)
      && (!machineType || m.section === machineType)
      && !usedMachineIds.has(m.id)
    )
  }, [machines, deptId, machineType, usedMachineIds])

  return (
    <Modal
      open={open} onCancel={onClose}
      title="New Machine Group"
      width={640}
      okText="Create" confirmLoading={submitting}
      onOk={() => {
        form.validateFields().then((v) => {
          if (!plantId) return
          onSubmit({
            plant_id: plantId,
            department_id: v.department_id,
            group_code: v.group_code,
            group_name: v.group_name,
            machine_type: v.machine_type,
            machine_ids: v.machine_ids ?? [],
            role_code: v.role_code ?? 'OPERATOR',
            required_operator_count: v.required_operator_count ?? 1,
            required_skill_level: v.required_skill_level ?? 0,
            critical_post_flag: !!v.critical_post_flag,
            certification_required: !!v.certification_required,
            effective_from: v.effective_from?.format('YYYY-MM-DD'),
            effective_to: v.effective_to?.format('YYYY-MM-DD') ?? null,
            remarks: v.remarks || null,
          })
        })
      }}
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="A group bundles N machines under one operator."
        description="Pick a department + machine type, then select the specific machines and the number of operators needed (usually 1)."
      />
      <Form form={form} layout="vertical" initialValues={{
        role_code: 'OPERATOR',
        required_operator_count: 1,
        required_skill_level: 0,
        effective_from: dayjs(),
      }}>
        <Space size={12} style={{ width: '100%' }} direction="vertical">
          <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              // Only depts with at least one active machine make sense
              // here — a Machine Group has no meaning for admin / store
              // / finance depts. Hide the rest so the dropdown stays
              // tight (Board of Directors, Plant Management, etc. used
              // to clutter the list).
              options={depts
                .filter((d) => machines.some((m) => m.department_id === d.id))
                .map((d) => ({ label: d.name, value: d.id }))}
              onChange={(v) => { setDeptId(v); form.setFieldsValue({ machine_ids: [] }) }}
            />
          </Form.Item>
          <Form.Item name="machine_type" label="Machine Type" rules={[{ required: true }]}
            tooltip={deptId
              ? 'Only machine types found in the selected department are listed'
              : 'Pick a department first'}>
            <Select
              placeholder={deptId
                ? (machineTypesForDept.length === 0
                    ? 'No machines available in this department'
                    : 'Pick the type to group')
                : 'Pick department first'}
              disabled={!deptId || machineTypesForDept.length === 0}
              options={machineTypesForDept}
              onChange={(v) => { setMachineType(v); form.setFieldsValue({ machine_ids: [] }) }}
            />
          </Form.Item>
          <Form.Item name="role_code" label="Role"
            rules={[{ required: true }]}
            tooltip="What role is allocated to this group? OPERATOR = primary runs the machines; GAITER = roving helper across the group (1 Gaiter per 4 RF machines); HELPER = generic.">
            <Select
              options={eligibleRoles.map((r) => ({
                label: (
                  <Space size={6}>
                    <Tag color={r.color_tag} style={{ margin: 0, fontSize: 10 }}>{r.role_code}</Tag>
                    <span>{r.role_name !== r.role_code ? r.role_name : ''}</span>
                  </Space>
                ),
                value: r.role_code,
              }))}
            />
          </Form.Item>
          <Form.Item name="group_code" label="Group Code (short)" rules={[{ required: true }]}>
            <Input placeholder="e.g. DFF-A or CARDS-LINE-1" />
          </Form.Item>
          <Form.Item name="group_name" label="Group Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Finisher Drawframes #1..#4" />
          </Form.Item>
          <Form.Item name="machine_ids" label="Machines in this group"
            rules={[{ required: true, message: 'Pick at least one machine' }]}
            tooltip="Only machines in the same dept + type that aren't already in another group">
            <Select
              mode="multiple" allowClear
              placeholder={deptId && machineType ? 'Pick machines' : 'Pick dept + type first'}
              disabled={!deptId || !machineType}
              options={eligibleMachines.map((m) => ({
                label: `${m.machine_id_str} · ${m.name}`,
                value: m.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="required_operator_count" label="Required operator count"
            tooltip="How many operators cover this group together. Usually 1.">
            <InputNumber min={1} max={20} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="required_skill_level" label="Minimum skill level (0-5)">
            <InputNumber min={0} max={5} style={{ width: 200 }} />
          </Form.Item>
          <Space>
            <Form.Item name="critical_post_flag" label="Critical Post"
              valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <Form.Item name="certification_required" label="Certification Required"
              valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="effective_from" label="Effective From" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective Till (optional)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  )
}


function EditGroupModal({
  group, onClose, machines, existingGroups,
  onSubmitPatch, onSubmitMembers, submitting,
}: {
  group: MachineGroup | null
  onClose: () => void
  machines: Machine[]
  existingGroups: MachineGroup[]
  onSubmitPatch: (id: string, body: MachineGroupPatch) => void
  onSubmitMembers: (id: string, machine_ids: string[]) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const [selectedMachineIds, setSelectedMachineIds] = useState<string[]>([])

  // Re-init form when opening a new group. Was: useState() with a side
  // effect — that's an anti-pattern (runs once on mount only) and risked
  // hook-count drift. useEffect is correct: re-runs every time group changes.
  useEffect(() => {
    if (group) {
      form.setFieldsValue({
        group_name: group.group_name,
        required_operator_count: group.required_operator_count,
        required_skill_level: group.required_skill_level,
        critical_post_flag: group.critical_post_flag,
        certification_required: group.certification_required,
        effective_to: group.effective_to ? dayjs(group.effective_to) : null,
        remarks: group.remarks ?? '',
      })
      setSelectedMachineIds(group.machine_ids)
    }
  }, [group, form])

  const usedByOthers = useMemo(() => {
    const s = new Set<string>()
    for (const g of existingGroups) {
      if (!g.is_active || (group && g.id === group.id)) continue
      for (const mid of g.machine_ids) s.add(mid)
    }
    return s
  }, [existingGroups, group])
  const eligibleMachines = useMemo(() => {
    if (!group) return []
    return machines.filter((m) =>
      m.department_id === group.department_id
      && m.section === group.machine_type
      && !usedByOthers.has(m.id)
    )
  }, [machines, group, usedByOthers])

  if (!group) return null

  return (
    <Modal
      open={!!group} onCancel={onClose}
      title={`Edit ${group.group_code} · ${group.group_name}`}
      width={640} okText="Save" confirmLoading={submitting}
      onOk={() => {
        form.validateFields().then((v) => {
          // Submit metadata first
          onSubmitPatch(group.id, {
            group_name: v.group_name,
            required_operator_count: v.required_operator_count,
            required_skill_level: v.required_skill_level,
            critical_post_flag: !!v.critical_post_flag,
            certification_required: !!v.certification_required,
            effective_to: v.effective_to?.format('YYYY-MM-DD') ?? null,
            remarks: v.remarks || null,
          })
          // If members changed, submit them too
          const sameSet = (a: string[], b: string[]) =>
            a.length === b.length && a.every((x) => b.includes(x))
          if (!sameSet(selectedMachineIds, group.machine_ids)) {
            onSubmitMembers(group.id, selectedMachineIds)
          }
        })
      }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{
        group_name: group.group_name,
        required_operator_count: group.required_operator_count,
        required_skill_level: group.required_skill_level,
        critical_post_flag: group.critical_post_flag,
        certification_required: group.certification_required,
        effective_to: group.effective_to ? dayjs(group.effective_to) : null,
        remarks: group.remarks ?? '',
      }}>
        <Form.Item label="Group Code (read-only)">
          <Input value={group.group_code} disabled />
        </Form.Item>
        <Form.Item label="Machine Type (read-only)">
          <Input value={group.machine_type} disabled />
        </Form.Item>
        <Form.Item name="group_name" label="Group Name">
          <Input />
        </Form.Item>
        <Form.Item label="Machines">
          <Select
            mode="multiple" allowClear
            value={selectedMachineIds}
            onChange={setSelectedMachineIds}
            options={eligibleMachines.map((m) => ({
              label: `${m.machine_id_str} · ${m.name}`,
              value: m.id,
            }))}
          />
        </Form.Item>
        <Form.Item name="required_operator_count" label="Required operator count">
          <InputNumber min={1} max={20} style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="required_skill_level" label="Minimum skill level (0-5)">
          <InputNumber min={0} max={5} style={{ width: 200 }} />
        </Form.Item>
        <Space>
          <Form.Item name="critical_post_flag" label="Critical Post"
            valuePropName="checked" style={{ marginBottom: 0 }}>
            <Switch />
          </Form.Item>
          <Form.Item name="certification_required" label="Certification Required"
            valuePropName="checked" style={{ marginBottom: 0 }}>
            <Switch />
          </Form.Item>
        </Space>
        <Form.Item name="effective_to" label="Effective Till">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
